/**
 * RailPanel — SRT Northeastern Line schedule at Sikhio station.
 *
 * Sikhio station (สถานีสีคิ้ว) — km 195 on the SRT Northeastern Line.
 * Shows upcoming departures in both directions.
 */

import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { Arrow } from "./Arrow";

export interface TrainService {
  id: string;
  trainNumber: string;
  trainType: "express" | "rapid" | "ordinary" | "special-express";
  origin: string;
  destination: string;
  scheduledDeparture: string;
  scheduledArrival: string | null;
  direction: "northbound" | "southbound";
  platformTrack: number | null;
  isRunningToday: boolean;
  note: string | null;
  source: string;
}

interface Props {
  data: TrainService[];
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const TYPE_COLOR: Record<string, string> = {
  "special-express": "var(--bad)",
  "express":         "var(--warn)",
  "rapid":           "var(--accent)",
  "ordinary":        "var(--text-3)",
};

const TYPE_LABEL: Record<string, string> = {
  "special-express": "ด่วนพิเศษ",
  "express":         "ด่วน",
  "rapid":           "รวดเร็ว",
  "ordinary":        "ธรรมดา",
};

export function RailPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  const trains = data.filter((t) => t.isRunningToday);

  if (loading && trains.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">รถไฟ / SRT SIKHIO</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
      </div>
    );
  }
  if (!trains.length) return null;

  const northbound = trains.filter((t) => t.direction === "northbound");
  const southbound = trains.filter((t) => t.direction === "southbound");

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="รถไฟ "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
      />
      <div className="eyebrow" style={{ marginTop: 4 }}>ขาขึ้น (<Arrow direction="right" /> อีสาน) · ถึง <Arrow direction="right" /> ออก</div>
      {northbound.map((t) => (
        <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, padding: "4px 0", borderBottom: "1px solid var(--line)" }}>
          <div style={{ minWidth: 0 }}>
            <span className="mono caption" style={{ color: TYPE_COLOR[t.trainType] ?? "var(--text-2)" }}>
              {t.trainNumber} · {TYPE_LABEL[t.trainType] ?? t.trainType}
            </span>
            <div className="caption" style={{ color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {t.origin} <Arrow direction="right" /> {t.destination}
            </div>
          </div>
          <span className="mono caption" style={{ color: "var(--text-1)", fontWeight: 600, flexShrink: 0 }}>
            {t.scheduledArrival ? `${t.scheduledArrival}<Arrow direction="right" />` : ""}{t.scheduledDeparture}
          </span>
        </div>
      ))}
      <div className="eyebrow" style={{ marginTop: 6 }}>ขาลง (<Arrow direction="right" /> กรุงเทพฯ) · ถึง <Arrow direction="right" /> ออก</div>
      {southbound.map((t) => (
        <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, padding: "4px 0", borderBottom: "1px solid var(--line)" }}>
          <div style={{ minWidth: 0 }}>
            <span className="mono caption" style={{ color: TYPE_COLOR[t.trainType] ?? "var(--text-2)" }}>
              {t.trainNumber} · {TYPE_LABEL[t.trainType] ?? t.trainType}
            </span>
            <div className="caption" style={{ color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {t.origin} <Arrow direction="right" /> {t.destination}
            </div>
          </div>
          <span className="mono caption" style={{ color: "var(--text-1)", fontWeight: 600, flexShrink: 0 }}>
            {t.scheduledArrival ? `${t.scheduledArrival}<Arrow direction="right" />` : ""}{t.scheduledDeparture}
          </span>
        </div>
      ))}
    </div>
  );
}
